import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Label,
  Input,
  Button,
  FormGroup,
} from 'reactstrap';

import { useSelector } from 'react-redux';
import styles from 'pages/Promotions/styles/promo.module.css';

import { useTranslation } from 'react-i18next';

import { RootState } from 'slices';
import { FieldArray, FormikProps } from 'formik';
import {
  ProductOptionValueForm,
  ProductOptionFormType,
} from 'types/product/productOptionTypes';
import {
  RequirementOptions,
  SelectionMethodOptions,
  SUB_OPTIONS_LIMIT,
} from 'pages/ProductOptions/ProductOptionConstants';
import Cleave from 'cleave.js/react';
import { useCallback, useEffect } from 'react';
import { postCheckProductOptionExists } from 'services/ProductOptionService';
import { setFormErrorCount, setNameExists } from 'slices/productOption/reducer';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { useDispatch } from 'react-redux';
import { RiDeleteBin2Line } from 'react-icons/ri';

const ProductOptionForm = ({
  formik,
  handleDeleteOption,
}: {
  formik: FormikProps<ProductOptionFormType>;
  handleDeleteOption: (productOpt: ProductOptionValueForm) => void;
}) => {
  const { t } = useTranslation();
  const dispatch: ThunkDispatch<RootState, void, Action> = useDispatch();

  const productOptionState = useSelector(
    (state: RootState) => state.ProductOption
  );

  const { showAllValidateErrors, NameExists } = productOptionState;

  const postCheckProductOption = useCallback(
    async (name: string) => {
      if (name === '') return;

      try {
        const response = await postCheckProductOptionExists(name);
        if (response) {
          const { message, error_code } = response;
          if (message === 'OK' && error_code === 0) {
            dispatch(setNameExists(false));
          } else {
            dispatch(setNameExists(true));
          }
        } else {
          dispatch(setNameExists(true));
          console.error('No response data returned');
        }
      } catch (error) {
        dispatch(setNameExists(true));
        console.error('Failed to check product option:', error);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(setFormErrorCount(Object.keys(formik.errors).length));
  }, [formik.errors, dispatch]);

  return (
    <Card>
      <CardHeader>
        <h5 className="card-title mb-0">{t('Product Option')}</h5>
      </CardHeader>
      <CardBody className={`${styles['card-body-pd']}`}>
        <Row>
          <Col lg={6}>
            <div className="mb-3 row">
              <Label htmlFor="name" className="col-sm-3 col-form-label">
                {t('Option')} <span style={{ color: 'red' }}>*</span>
              </Label>
              <Col sm={9}>
                <div className="mt-2">
                  <Input
                    type="text"
                    className="form-control"
                    id="name"
                    name="name"
                    placeholder={t('Type')}
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    // onBlur={formik.handleBlur}
                    onBlur={(e) => {
                      postCheckProductOption(formik.values.name);
                      formik.handleBlur(e);
                    }}
                    maxLength={50}
                    required
                  />

                  {(formik.touched.name &&
                    formik.errors.name &&
                    typeof formik.errors.name === 'string') ||
                  (formik.isValid === false &&
                    formik.errors.name &&
                    showAllValidateErrors === true) ? (
                    <div className="text-danger mt-1">{formik.errors.name}</div>
                  ) : NameExists ? (
                    <div className="text-danger mt-1">
                      {t('Product option name already exists.')}
                    </div>
                  ) : null}
                  {/* {(formik.touched.name &&
                    formik.errors.name &&
                    typeof formik.errors.name === 'string') ||
                  (formik.isValid === false &&
                    formik.errors.name &&
                    showAllValidateErrors === true) ? (
                    <div className="text-danger mt-1">{formik.errors.name}</div>
                  ) : null} */}
                </div>
              </Col>
            </div>

            <div className="mb-3 row">
              <Label htmlFor="forceType" className="col-sm-3 col-form-label">
                {t('Required Selection')}{' '}
                <span style={{ color: 'red' }}>*</span>
              </Label>
              <Col sm={9}>
                <div className="">
                  <FormGroup key={4}>
                    {RequirementOptions(t).map(
                      (p: { label: string; value: string }, _index) => (
                        <div
                          key={p.value}
                          className="form-check form-check-inline"
                        >
                          <Input
                            className="form-check-input"
                            type="radio"
                            name="forceType"
                            id={p.label}
                            value={p.value}
                            checked={formik.values.forceType === p.value}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label className="form-check-label" htmlFor={p.label}>
                            {p.label}
                          </Label>
                        </div>
                      )
                    )}
                  </FormGroup>
                </div>
              </Col>
            </div>

            <div className="mb-3 row">
              <Label htmlFor="optionType" className="col-sm-3 col-form-label">
                {t('Selection Method')} <span style={{ color: 'red' }}>*</span>
              </Label>
              <Col sm={9}>
                <div className="mt-2">
                  <FormGroup key={4}>
                    {SelectionMethodOptions(t).map(
                      (p: { label: string; value: string }, _index) => (
                        <div
                          key={p.value}
                          className="form-check form-check-inline"
                        >
                          <Input
                            className="form-check-input"
                            type="radio"
                            name="optionType"
                            id={p.label}
                            value={p.value}
                            checked={formik.values.optionType === p.value}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label className="form-check-label" htmlFor={p.label}>
                            {p.label}
                          </Label>
                        </div>
                      )
                    )}
                  </FormGroup>
                </div>
              </Col>
            </div>
          </Col>
          <Col lg={6}></Col>
        </Row>
        <Row>
          <Col lg={6}>
            <FieldArray
              name="options"
              render={(arrayHelpers) => (
                <Row>
                  <Col xs={0} sm={3} className="mb-2 mb-xs-0"></Col>
                  <Col xs={12} sm={9} className="mb-2 mb-xs-0">
                    <Card className={`${styles['border-dashed']}`}>
                      <CardBody className={`${styles['card-body-pd']}`}>
                        <Row className="align-items-center mb-3 mb-xs-0 justify-content-center">
                          <Col xs={5} sm={5} className="mb-2 mb-xs-0">
                            <Label className={`${styles['text-pd']}`}>
                              {t('Sub-option')}
                            </Label>
                          </Col>
                          <Col xs={5} sm={5} className="mb-2 mb-xs-0">
                            <Label style={{ paddingLeft: '4px' }}>
                              {t('Additional Price')}
                            </Label>
                          </Col>
                          <Col xs={2} sm={2} className="mb-2 mb-xs-0"></Col>
                        </Row>

                        {formik.values.options.map(
                          (option: ProductOptionValueForm, index: number) => (
                            <Row
                              key={index}
                              className="align-items-top mb-3 mb-xs-0"
                            >
                              <Col xs={5} sm={5} className="mb-2 mb-xs-0">
                                <div className="input-container">
                                  <Input
                                    type="text"
                                    name={`options[${index}].name`}
                                    value={formik.values.options[index].name}
                                    // placeholder={t('ProductField_ItemName')}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    className={`form-control`}
                                    maxLength={50}
                                  />

                                  {(formik.touched.options?.[index]?.name &&
                                    formik.errors.options?.[index] &&
                                    typeof formik.errors.options[index] ===
                                      'object' &&
                                    'name' in formik.errors.options[index] &&
                                    typeof formik.errors.options[index].name ===
                                      'string') ||
                                  (formik.isValid === false &&
                                    formik.errors.options?.[index] &&
                                    typeof formik.errors.options[index] ===
                                      'object' &&
                                    'name' in formik.errors.options[index] &&
                                    typeof formik.errors.options[index].name ===
                                      'string' &&
                                    showAllValidateErrors === true) ? (
                                    <div className="text-danger">
                                      {formik.errors.options[index].name}
                                    </div>
                                  ) : null}
                                </div>
                              </Col>
                              <Col xs={5} sm={5} className="mb-2 mb-xs-0">
                                <div className="input-container">
                                  {/* <Input
                                    type="number"
                                    name={`options[${index}].additionalPrice`}
                                    value={
                                      formik.values.options[index]
                                        .additionalPrice
                                    }
                                    // placeholder={t('additionalPrice')}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    className={`form-control ${styles['no-px']}`}
                                  /> */}

                                  <Cleave
                                    className={`form-control ${styles['no-px']}`}
                                    name={`options[${index}].additionalPrice`}
                                    value={
                                      formik.values.options[index]
                                        .additionalPrice
                                    }
                                    options={{
                                      numeral: true,
                                      numeralDecimalScale: 3,
                                      numeralThousandsGroupStyle: 'thousand',
                                    }}
                                    onChange={formik.handleChange}
                                    // onBlur={formik.handleBlur}
                                    onBlur={(e: {
                                      target: { name: any; value: string };
                                    }) => {
                                      formik.handleBlur(e);
                                      const name = e.target.name;
                                      const raw = e.target.value.replace(
                                        /,/g,
                                        ''
                                      ); // remove commas
                                      const value = parseFloat(raw);

                                      if (!isNaN(value)) {
                                        // Check if it's an integer (e.g., 5)
                                        if (Number.isInteger(value)) {
                                          formik.setFieldValue(
                                            name,
                                            value.toFixed(2)
                                          ); // convert to "5.00"
                                        } else {
                                          // If it's already a float (e.g., 5.123), keep it as-is
                                          formik.setFieldValue(name, raw);
                                        }
                                      }
                                    }}
                                  />

                                  {(formik.touched.options?.[index]
                                    ?.additionalPrice &&
                                    formik.errors.options?.[index] &&
                                    typeof formik.errors.options[index] ===
                                      'object' &&
                                    'additionalPrice' in
                                      formik.errors.options[index] &&
                                    typeof formik.errors.options[index]
                                      .additionalPrice === 'string') ||
                                  (formik.isValid === false &&
                                    formik.errors.options?.[index] &&
                                    typeof formik.errors.options[index] ===
                                      'object' &&
                                    'additionalPrice' in
                                      formik.errors.options[index] &&
                                    typeof formik.errors.options[index]
                                      .additionalPrice === 'string' &&
                                    showAllValidateErrors === true) ? (
                                    <div className="text-danger">
                                      {
                                        formik.errors.options[index]
                                          .additionalPrice
                                      }
                                    </div>
                                  ) : null}
                                </div>
                              </Col>
                              <Col
                                xs={2}
                                sm={2}
                                className="d-flex align-items-center mb-2 mb-xs-0"
                                style={{
                                  padding: '0.5rem 0.9rem',
                                }}
                              >
                                {formik.values.options.length > 0 && (
                                  <div
                                    onClick={async () => {
                                      if (option.id) {
                                        handleDeleteOption(option);
                                      }
                                      arrayHelpers.remove(index);
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    style={{
                                      cursor: 'pointer',
                                    }}
                                    className="d-flex align-items-center justify-content-center"
                                  >
                                    <RiDeleteBin2Line
                                      size={'20px'}
                                      color="red"
                                    />
                                  </div>
                                )}
                              </Col>
                            </Row>
                          )
                        )}

                        {formik.errors.options &&
                          typeof formik.errors.options === 'string' && (
                            <div className="text-danger mb-1">
                              {formik.errors.options}
                            </div>
                          )}

                        <Row>
                          <Col sm={12} xs={12}>
                            <div className={`${styles['text-pd']}`}>
                              <Button
                                className="btn me-2"
                                type="button"
                                color="success"
                                onClick={() =>
                                  formik.values.options.length <
                                    SUB_OPTIONS_LIMIT &&
                                  arrayHelpers.push({
                                    name: '',
                                    additionalPrice: '0.00',
                                  })
                                }
                                disabled={
                                  formik.values.options.length >=
                                  SUB_OPTIONS_LIMIT
                                }
                              >
                                + {t('Btn_Add')}
                              </Button>
                              {formik.values.options.length >=
                                SUB_OPTIONS_LIMIT && (
                                <div className="text-danger mt-2">
                                  {t('ProductPage_Warn_Must_not_exceed')}{' '}
                                  {SUB_OPTIONS_LIMIT}{' '}
                                  {t('ProductPage_Warn_items')}
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}
            />

            {/* <div className="mb-3 row">
              <Label htmlFor="active" className="col-sm-3 col-form-label">
                {t('Active Status')} <span style={{ color: 'red' }}>*</span>
              </Label>
              <Col sm={9}>
                <div className="">
                  <FormGroup key={4}>
                    {ActiveStatusOptions(t).map(
                      (p: { label: string; value: string }, _index) => (
                        <div
                          key={p.value}
                          className="form-check form-check-inline"
                        >
                          <Input
                            className="form-check-input"
                            type="radio"
                            name="active"
                            id={p.label}
                            value={p.value}
                            checked={formik.values.active === p.value}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label className="form-check-label" htmlFor={p.label}>
                            {p.label}
                          </Label>
                        </div>
                      )
                    )}
                  </FormGroup>
                </div>
              </Col>
            </div> */}
          </Col>
          <Col lg={6}></Col>
        </Row>
      </CardBody>
    </Card>
  );
};
export default ProductOptionForm;
